import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useStreakPinger } from "@/lib/streak";
import { Toaster } from "sonner";
import "@/App.css";

import Nav from "@/components/Nav";
import DoubtFab from "@/components/DoubtFab";
import ModuleBackdrop from "@/components/ModuleBackdrop";
import MilestoneUnlockModal from "@/components/MilestoneUnlockModal";
import { PageWrap } from "@/components/motion";
import Home from "@/pages/Home";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import Meghalaya from "@/pages/Meghalaya";
import MeghalayaDistrict from "@/pages/MeghalayaDistrict";
import CommunityBoard from "@/pages/CommunityBoard";
import PublicProfile from "@/pages/PublicProfile";
import StudyHub from "@/pages/StudyHub";
import DiagnosticTest from "@/pages/DiagnosticTest";
import DoubtChat from "@/pages/DoubtChat";
import DoubtCorner from "@/pages/DoubtCorner";
import ResultHistory from "@/pages/ResultHistory";
import Bookmarks from "@/pages/Bookmarks";
import BookmarkTake from "@/pages/BookmarkTake";
import { StudyPlansList, StudyPlanDetail } from "@/pages/StudyPlans";
import CareerHub from "@/pages/CareerHub";
import InterestAssessment from "@/pages/InterestAssessment";
import CareerPath from "@/pages/CareerPath";
import Mentors from "@/pages/Mentors";
import EntreHub from "@/pages/EntreHub";
import SubmitIdea from "@/pages/SubmitIdea";
import IdeasExplore from "@/pages/IdeasExplore";
import Challenge from "@/pages/Challenge";
import SkillsHub from "@/pages/SkillsHub";
import SkillTrack from "@/pages/SkillTrack";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-heading text-2xl">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AnimatedRoutes() {
  const loc = useLocation();
  const { user, setUser } = useAuth();
  const { justUnlocked, clearOne } = useStreakPinger(user, setUser);
  return (
    <>
      <MilestoneUnlockModal milestone={justUnlocked[0] || null} onClose={clearOne} />
      <AnimatePresence mode="wait">
        <Routes location={loc} key={loc.pathname}>
        <Route path="/" element={<PageWrap><Home /></PageWrap>} />
        <Route path="/auth" element={<PageWrap><Auth /></PageWrap>} />
        <Route path="/auth/forgot" element={<PageWrap><ForgotPassword /></PageWrap>} />
        <Route path="/auth/reset" element={<PageWrap><ResetPassword /></PageWrap>} />
        <Route path="/auth/callback" element={<PageWrap><AuthCallback /></PageWrap>} />
        <Route path="/profile" element={<Protected><PageWrap><Profile /></PageWrap></Protected>} />
        <Route path="/meghalaya" element={<PageWrap><Meghalaya /></PageWrap>} />
        <Route path="/meghalaya/:districtId" element={<PageWrap><MeghalayaDistrict /></PageWrap>} />
        <Route path="/community" element={<PageWrap><CommunityBoard /></PageWrap>} />
        <Route path="/u/:userId" element={<PageWrap><PublicProfile /></PageWrap>} />

        <Route path="/study" element={<PageWrap><StudyHub /></PageWrap>} />
        <Route path="/study/diagnostic" element={<PageWrap><DiagnosticTest /></PageWrap>} />
        <Route path="/study/chat" element={<PageWrap><DoubtChat /></PageWrap>} />
        <Route path="/study/corner" element={<Protected><PageWrap><DoubtCorner /></PageWrap></Protected>} />
        <Route path="/study/history" element={<Protected><PageWrap><ResultHistory /></PageWrap></Protected>} />
        <Route path="/study/bookmarks" element={<Protected><PageWrap><Bookmarks /></PageWrap></Protected>} />
        <Route path="/study/bookmark/:id" element={<PageWrap><BookmarkTake /></PageWrap>} />
        <Route path="/study/plans" element={<Protected><PageWrap><StudyPlansList /></PageWrap></Protected>} />
        <Route path="/study/plans/:id" element={<Protected><PageWrap><StudyPlanDetail /></PageWrap></Protected>} />

        <Route path="/career" element={<PageWrap><CareerHub /></PageWrap>} />
        <Route path="/career/assess" element={<PageWrap><InterestAssessment /></PageWrap>} />
        <Route path="/career/path/:id" element={<PageWrap><CareerPath /></PageWrap>} />
        <Route path="/career/mentors" element={<PageWrap><Mentors /></PageWrap>} />

        <Route path="/ideas" element={<PageWrap><EntreHub /></PageWrap>} />
        <Route path="/ideas/submit" element={<Protected><PageWrap><SubmitIdea /></PageWrap></Protected>} />
        <Route path="/ideas/explore" element={<PageWrap><IdeasExplore /></PageWrap>} />
        <Route path="/ideas/challenge" element={<PageWrap><Challenge /></PageWrap>} />

        <Route path="/skills" element={<PageWrap><SkillsHub /></PageWrap>} />
        <Route path="/skills/:id" element={<PageWrap><SkillTrack /></PageWrap>} />
      </Routes>
    </AnimatePresence>
    </>
  );
}

function Shell() {
  return (
    <div className="App">
      <ModuleBackdrop />
      <Nav />
      <div style={{ position: "relative", zIndex: 1 }}>
        <AnimatedRoutes />
      </div>
      <DoubtFab />
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}
